import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, CalendarOff } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { AbsStatus, AbsChip, frd } from "./common";
import { AbsenceActions, UnassignedNotice } from "./AgentAbsencesPanel";
import { useRealtime } from "../../context/RealtimeContext";

const pad = (n) => String(n).padStart(2, "0");
const monthDays = (y, m) => Array.from({ length: new Date(y, m + 1, 0).getDate() }, (_, i) => `${y}-${pad(m + 1)}-${pad(i + 1)}`);

const Grid = ({ items, days }) => {
  const rows = useMemo(() => {
    const m = {};
    items.forEach((a) => { const r = (m[a.agent_id] ||= { name: a.agent_name, by: {} }); a.days.forEach((d) => { r.by[d] = a; }); });
    return Object.entries(m).sort((x, y) => x[1].name.localeCompare(y[1].name));
  }, [items]);
  if (!rows.length) return <p className="text-sm text-slate-500 py-6 text-center" data-testid="abs-cal-empty">Aucune absence sur ce mois.</p>;
  return (
    <div className="overflow-auto border border-slate-200 rounded-md" data-testid="abs-cal-grid">
      <table className="text-xs">
        <thead><tr className="bg-slate-50"><th className="sticky left-0 bg-slate-50 px-2 py-1 text-left min-w-[150px]">Agent</th>
          {days.map((d) => { const w = new Date(`${d}T12:00:00`).getDay(); return <th key={d} className={`px-1 py-1 min-w-[30px] ${w === 0 ? "text-indigo-700 bg-indigo-50" : ""}`}>{d.slice(8)}</th>; })}</tr></thead>
        <tbody>{rows.map(([aid, r]) => (
          <tr key={aid} className="border-t border-slate-100">
            <td className="sticky left-0 bg-white px-2 py-1 font-semibold whitespace-nowrap">{r.name}</td>
            {days.map((d) => <td key={d} className="px-0.5 py-0.5 text-center" data-testid={`abs-cal-${aid}-${d}`}>{r.by[d] ? <AbsChip a={{ ...r.by[d], agent_name: "" }} /> : null}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
};

export const AbsencesCalendar = ({ onClose }) => {
  const now = new Date();
  const [cur, setCur] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [site, setSite] = useState("");
  const [sites, setSites] = useState([]);
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [tab, setTab] = useState("cal");
  const [un, setUn] = useState(null);
  const { lastEvent } = useRealtime();
  const days = monthDays(cur.y, cur.m);
  const load = useCallback(() => {
    const p = { start: days[0], end: days[days.length - 1], status: "approved,pending", ...(site ? { site_id: site } : {}) };
    Promise.all([http.get("/absences", { params: p }), http.get("/absences", { params: { status: "pending", ...(site ? { site_id: site } : {}) } })])
      .then(([a, b]) => { setItems(a.data); setPending(b.data); }).catch((e) => toast.error(formatApiError(e)));
  }, [days[0], site]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (lastEvent?.event === "absence_changed") load(); }, [lastEvent, load]);
  useEffect(() => { http.get("/sites").then((r) => setSites(r.data.filter((s) => !s.archived))).catch((e) => toast.error(formatApiError(e))); }, []);
  const move = (k) => setCur(({ y, m }) => ({ y: m + k < 0 ? y - 1 : m + k > 11 ? y + 1 : y, m: (m + k + 12) % 12 }));
  const label = new Date(cur.y, cur.m, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-6xl max-h-[92vh] flex flex-col" data-testid="absences-calendar">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
          <CalendarOff className="h-5 w-5 text-sky-700" /><h3 className="font-display text-lg font-bold flex-1">Absences de l'équipe</h3>
          <button onClick={onClose} data-testid="absences-calendar-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-b border-slate-100">
          {[["cal", "Calendrier"], ["pending", `Demandes en attente (${pending.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`abs-tab-${k}`} className={`px-3 py-1.5 rounded-md text-xs font-bold ${tab === k ? "bg-slate-900 text-white" : "border border-slate-300"}`}>{l}</button>
          ))}
          <select data-testid="abs-cal-site" value={site} onChange={(e) => setSite(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1.5 text-sm ml-auto">
            <option value="">Tous les sites</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {tab === "cal" && <div className="flex items-center gap-1">
            <button onClick={() => move(-1)} data-testid="abs-cal-prev" className="p-1 rounded hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-sm font-bold capitalize w-36 text-center" data-testid="abs-cal-month">{label}</span>
            <button onClick={() => move(1)} data-testid="abs-cal-next" className="p-1 rounded hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
          </div>}
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          <UnassignedNotice items={un} onClose={() => setUn(null)} />
          {tab === "cal" ? <Grid items={items} days={days} /> : pending.length === 0 ? <p className="text-sm text-slate-500" data-testid="abs-pending-empty">Aucune demande en attente.</p> : (
            <ul className="border border-slate-200 rounded-md divide-y" data-testid="abs-pending-list">
              {pending.map((a) => (
                <li key={a.id} className="px-3 py-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`abs-pending-${a.id}`}>
                  <b>{a.agent_name}</b><span>{a.label} du {frd(a.start)} au {frd(a.end)} · {a.day_count} j</span><AbsStatus s={a.status} id={a.id} />
                  {a.note && <span className="text-xs text-slate-500">« {a.note} »</span>}
                  <span className="ml-auto"><AbsenceActions a={a} onDone={(u) => { setUn(u || null); load(); }} /></span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
